import { Button, ExternalLinkTwoToneIcon } from "@relex-solutions/fragments"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useGetAiDescription } from "@/domains/customers/hooks/useGetAiDescription"
import type { Customer } from "@/domains/customers/types/Customer"
import { GraphPreview } from "@/domains/visualization/components/GraphPreview/GraphPreview"
import Card from "@/shared/components/card/Card"
import { useFeatureFlags } from "@/shared/hooks/useFeatureFlags"
import ModuleCardContent from "../ModuleCardContent/ModuleCardContent"
import styles from "./CustomerContent.module.css"

interface CustomerContentProps {
  customer: Customer
}

const links = [
  {
    title: "CTA PLAN (placeholder)",
    href: "https://www.relexsolutions.com/"
  },
  {
    title: "Customer scorecard (placeholder)",
    href: "https://www.relexsolutions.com/"
  },
  {
    title: "Jira kanban board (placeholder)",
    href: "https://www.relexsolutions.com/"
  },
  {
    title: "Sharepoint (placeholder)",
    href: "https://www.relexsolutions.com/"
  },
  {
    title: "Hubsport (placeholder)",
    href: "https://www.relexsolutions.com/"
  },
  {
    title: "Customer Account Plan (placeholder)",
    href: "https://www.relexsolutions.com/"
  }
]

// Layout configuration based on whether placeholder content is shown
const getLayoutConfig = (showPlaceholderContent: boolean) => {
  if (showPlaceholderContent) {
    return {
      description: "col-span-12 lg:col-span-8 xl:col-span-8",
      customerTime: "col-span-12 lg:col-span-4 xl:col-span-2",
      hierarchy: "col-span-12 lg:col-span-12 xl:col-span-8",
      modulesDev: "col-span-12 lg:col-span-6 xl:col-span-5",
      links: "col-span-12 lg:col-span-6 xl:col-span-5"
    }
  } else {
    return {
      description: "col-span-12 xl:col-span-5",
      modulesProd: "col-span-12 xl:col-span-5",
      hierarchy: "col-span-12 xl:col-span-10"
    }
  }
}

const getCardConfigs = (config: ReturnType<typeof getLayoutConfig>) => ({
  description: {
    showInProduction: true,
    classes: config.description
  },
  customerTime: {
    showInProduction: false,
    classes: config.customerTime ?? ""
  },
  hierarchy: {
    showInProduction: true,
    classes: config.hierarchy
  },
  modulesDev: {
    showInProduction: false,
    classes: config.modulesDev ?? ""
  },
  modulesProd: {
    showInProduction: true,
    classes: config.modulesProd ?? ""
  },
  links: {
    showInProduction: false,
    classes: config.links ?? ""
  }
})

export function CustomerContent({ customer }: CustomerContentProps) {
  const { t } = useTranslation()
  const [description, setDescription] = useState("")
  const { generateDescription, isLoading, isError } = useGetAiDescription(customer.id)
  const featureFlags = useFeatureFlags()
  const { showPlaceholderContent } = featureFlags

  const layoutConfig = useMemo(() => getLayoutConfig(showPlaceholderContent), [showPlaceholderContent])
  const cardConfigs = useMemo(() => getCardConfigs(layoutConfig), [layoutConfig])

  const shouldShowCard = useCallback(
    (cardId: keyof typeof cardConfigs) => {
      const cardConfig = cardConfigs[cardId]

      if (showPlaceholderContent) {
        return !cardConfig.showInProduction
      }

      return cardConfig.showInProduction
    },
    [cardConfigs, showPlaceholderContent]
  )

  const handleGenerateDescription = useCallback(() => {
    generateDescription(undefined, {
      onSuccess: (data: string) => {
        setDescription(data)
      }
    })
  }, [generateDescription])

  const headerAction = useMemo(() => {
    if (!showPlaceholderContent) return undefined

    return (
      <div className={styles.link}>
        <a href="https://www.relexsolutions.com/" className={styles.linkText} target="_blank" rel="noreferrer">
          {customer.name}
        </a>
        <ExternalLinkTwoToneIcon className={styles.linkIcon} />
      </div>
    )
  }, [showPlaceholderContent, customer.name])

  return (
    <div className={`grid grid-cols-12 gap-4 ${styles.container}`}>
      <Card
        title={t("customer.overview.descriptionTitle")}
        className={cardConfigs.description.classes}
        headerAction={headerAction}
      >
        <div>
          {description ? (
            <p className={styles.description}>{description}</p>
          ) : (
            <div className={styles.descriptionContainer}>
              <p className={styles.descriptionPlaceholder}>{t("customer.overview.noDescription")}</p>
              <Button variant="primary" onClick={handleGenerateDescription} isLoading={isLoading} disabled={isLoading}>
                {t("customer.overview.generateDescription")}
              </Button>
              {isError && <p className={styles.error}>{t("customer.overview.descriptionError")}</p>}
            </div>
          )}
        </div>
      </Card>
      {shouldShowCard("modulesProd") && (
        <Card title={t("customer.overview.modulesTitle")} className={cardConfigs.modulesProd.classes}>
          <ModuleCardContent />
        </Card>
      )}
      {shouldShowCard("customerTime") && (
        <Card title={t("customer.overview.customerTime")} className={cardConfigs.customerTime.classes}>
          <p className={`${styles.customerTime} fr-text-500`}>14:20 (placeholder)</p>
          <p className={`${styles.customerTime} fr-text-300-strong`}>(GMT+2)</p>
        </Card>
      )}
      <Card title={t("customer.overview.hierarchyTitle")} className={cardConfigs.hierarchy.classes}>
        <GraphPreview />
      </Card>
      {shouldShowCard("modulesDev") && (
        <Card title={t("customer.overview.modulesTitle")} className={cardConfigs.modulesDev.classes}>
          <ModuleCardContent />
        </Card>
      )}
      {shouldShowCard("links") && (
        <Card title={t("customer.overview.linksTitle")} className={cardConfigs.links.classes}>
          <div className={styles.linksContainer}>
            {links.map((link) => (
              <a href={link.href} key={link.title} className={styles.linkText} target="_blank" rel="noreferrer">
                {link.title}
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
